import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faCheck,
  faUndo,
  faPaintBrush,
} from "@fortawesome/free-solid-svg-icons";
import InlineFieldTooltip from "./InlineFieldTooltip";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const roundToTwo = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : 0;
};

const toNumber = (value) => {
  if (value === null || value === undefined) return null;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const formatNumber = (value, decimals = 2) => {
  const num = toNumber(value);
  if (num === null) return "-";
  return num.toLocaleString("en-CA", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const getElementIdentifier = (element, fallbackLabel = "Element") =>
  element?.element_name ||
  element?.span_name ||
  element?.element_code ||
  element?.span_code ||
  fallbackLabel;

const buildPaintedAreaRow = (element, fallbackLabel, index) => {
  const totalArea = toNumber(element?.total_area) || 0;
  const estimatedPaintedArea =
    toNumber(element?.estimated_painted_area) ??
    toNumber(element?.estimated_repair_area) ??
    0;
  const estimatedPaintedPercentage =
    totalArea > 0 ? roundToTwo((estimatedPaintedArea / totalArea) * 100) : 0;
  const savedActualPaintedArea = toNumber(element?.actual_painted_area);
  const savedActualPaintedPercentage = toNumber(
    element?.actual_painted_area_percent,
  );
  const hasSavedOverride =
    savedActualPaintedArea !== null || savedActualPaintedPercentage !== null;
  const nextActualPaintedArea = hasSavedOverride
    ? (savedActualPaintedArea ??
      (totalArea > 0 && savedActualPaintedPercentage !== null
        ? roundToTwo((totalArea * savedActualPaintedPercentage) / 100)
        : estimatedPaintedArea))
    : estimatedPaintedArea;

  return {
    key:
      element?.project_element_id ||
      element?.element_id ||
      element?.span_id ||
      element?.id ||
      `paint-row-${index}`,
    elementId: element?.element_id || element?.span_id || element?.id || null,
    identifier: getElementIdentifier(element, `${fallbackLabel} ${index + 1}`),
    totalArea,
    estimatedPaintedArea: roundToTwo(estimatedPaintedArea),
    estimatedPaintedPercentage,
    actualPaintedArea: roundToTwo(nextActualPaintedArea),
    actualPaintedPercentage:
      totalArea > 0 ? roundToTwo((nextActualPaintedArea / totalArea) * 100) : 0,
  };
};

const PanelBayPaintingModal = ({
  isOpen,
  span,
  onClose,
  onSave,
  itemLabelSingular = "Element",
  submitButtonLabel = "Save Changes",
  showMarkCompletedButton = false,
}) => {
  const [paintRows, setPaintRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [activeSubmitMode, setActiveSubmitMode] = useState(null);
  const isCompletionFlow = submitButtonLabel === "Mark Completed";
  const canShowFooterCompletionActions =
    showMarkCompletedButton && !isCompletionFlow;

  const itemLabelLower = itemLabelSingular.toLowerCase();
  const selectionItems = useMemo(() => {
    if (!span) {
      return [];
    }

    return Array.isArray(span?.items) ? span.items : [span];
  }, [span]);
  const isGroupSelection =
    Boolean(span?.isGroupSelection) || Array.isArray(span?.items);
  const targetIdentifier = useMemo(() => {
    if (span?.label) {
      return span.label;
    }

    if (span?.targetLabel) {
      return span.targetLabel;
    }

    if (isGroupSelection) {
      return (
        span?.parent_name ||
        span?.frame_name ||
        getElementIdentifier(selectionItems[0], `selected ${itemLabelLower}`)
      );
    }

    return getElementIdentifier(span, itemLabelSingular);
  }, [
    isGroupSelection,
    itemLabelLower,
    itemLabelSingular,
    selectionItems,
    span,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setPaintRows(
      selectionItems.map((element, index) =>
        buildPaintedAreaRow(element, itemLabelSingular, index),
      ),
    );
    setSaving(false);
    setActiveSubmitMode(null);
    setError("");
  }, [isOpen, itemLabelSingular, selectionItems]);

  const totals = useMemo(
    () =>
      paintRows.reduce(
        (summary, row) => ({
          totalArea: summary.totalArea + row.totalArea,
          estimatedPaintedArea:
            summary.estimatedPaintedArea + row.estimatedPaintedArea,
          actualPaintedArea: summary.actualPaintedArea + row.actualPaintedArea,
        }),
        {
          totalArea: 0,
          estimatedPaintedArea: 0,
          actualPaintedArea: 0,
        },
      ),
    [paintRows],
  );
  const totalEstimatedPaintedPercentage =
    totals.totalArea > 0
      ? roundToTwo((totals.estimatedPaintedArea / totals.totalArea) * 100)
      : 0;
  const totalActualPaintedPercentage =
    totals.totalArea > 0
      ? roundToTwo((totals.actualPaintedArea / totals.totalArea) * 100)
      : 0;
  const paintedAreaVariance = roundToTwo(
    totals.actualPaintedArea - totals.estimatedPaintedArea,
  );
  const paintedAreaVarianceLabel =
    paintedAreaVariance > 0.01
      ? "More than estimated"
      : paintedAreaVariance < -0.01
        ? "Less than estimated"
        : "Matches estimate";
  const paintedAreaVarianceColor =
    paintedAreaVariance > 0.01
      ? "text-amber-600"
      : paintedAreaVariance < -0.01
        ? "text-emerald-600"
        : "text-blue-600";

  const updatePaintRow = (rowKey, updater) => {
    setPaintRows((currentRows) =>
      currentRows.map((row) => (row.key === rowKey ? updater(row) : row)),
    );
  };

  const syncActualPaintedArea = (rowKey, nextPaintedArea) => {
    updatePaintRow(rowKey, (row) => {
      const normalizedPaintedArea = roundToTwo(
        clamp(Number(nextPaintedArea) || 0, 0, row.totalArea || 0),
      );
      const normalizedPaintedPercentage =
        row.totalArea > 0
          ? roundToTwo((normalizedPaintedArea / row.totalArea) * 100)
          : 0;

      return {
        ...row,
        actualPaintedArea: normalizedPaintedArea,
        actualPaintedPercentage: normalizedPaintedPercentage,
      };
    });
  };

  const syncActualPaintedPercentage = (rowKey, nextPaintedPercentage) => {
    updatePaintRow(rowKey, (row) => {
      const normalizedPaintedPercentage = roundToTwo(
        clamp(Number(nextPaintedPercentage) || 0, 0, 100),
      );
      const normalizedPaintedArea =
        row.totalArea > 0
          ? roundToTwo((row.totalArea * normalizedPaintedPercentage) / 100)
          : 0;

      return {
        ...row,
        actualPaintedArea: normalizedPaintedArea,
        actualPaintedPercentage: normalizedPaintedPercentage,
      };
    });
  };

  const adjustPaintedPercentage = (rowKey, delta) => {
    updatePaintRow(rowKey, (row) => {
      const normalizedPaintedPercentage = roundToTwo(
        clamp(Number(row.actualPaintedPercentage || 0) + delta, 0, 100),
      );
      const normalizedPaintedArea =
        row.totalArea > 0
          ? roundToTwo((row.totalArea * normalizedPaintedPercentage) / 100)
          : 0;

      return {
        ...row,
        actualPaintedArea: normalizedPaintedArea,
        actualPaintedPercentage: normalizedPaintedPercentage,
      };
    });
  };

  const resetToEstimated = (rowKey) => {
    updatePaintRow(rowKey, (row) => ({
      ...row,
      actualPaintedArea: row.estimatedPaintedArea,
      actualPaintedPercentage: row.estimatedPaintedPercentage,
    }));
  };

  const handleSave = async (
    actionMode = isCompletionFlow ? "complete" : "save",
  ) => {
    if (!onSave || paintRows.length === 0) return;

    const rowsToSubmit = paintRows
      .filter((row) => row.elementId !== null && row.elementId !== undefined)
      .map((row) => ({
        elementId: row.elementId,
        actualPaintedArea:
          actionMode === "undamaged" ? 0 : row.actualPaintedArea,
        actualPaintedAreaPercent:
          actionMode === "undamaged" ? 0 : row.actualPaintedPercentage,
      }));

    setSaving(true);
    setActiveSubmitMode(actionMode);
    setError("");

    try {
      await onSave(rowsToSubmit, actionMode);
    } catch (err) {
      setError(err?.message || "Failed to save painted area updates");
      setSaving(false);
      setActiveSubmitMode(null);
      return;
    }

    setSaving(false);
    setActiveSubmitMode(null);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
              <FontAwesomeIcon icon={faPaintBrush} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Painted Area
              </h2>
              <p className="text-sm text-gray-600">
                {isGroupSelection
                  ? `Record the actual painted area for each ${itemLabelLower} under `
                  : "Record the actual painted area for "}
                <span className="font-medium">{targetIdentifier}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 transition-colors hover:text-gray-700"
            type="button"
          >
            <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4">
          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="rounded-lg border border-gray-200 p-4">
            <div className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-1">
              <h3 className="text-sm font-semibold text-gray-900">
                Coverage &amp; Painted Area
              </h3>
              <InlineFieldTooltip
                page="repair_projects"
                section="painted_area_modal"
                fieldKey="coverage_and_painted_area"
              />
              {isCompletionFlow && (
                <p className="text-sm text-gray-600">
                  Review &amp; Adjust Actual Painted Area, then press Mark
                  Completed, or Cancel.
                </p>
              )}
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full table-fixed border-collapse text-sm">
                <colgroup>
                  <col style={{ width: "30%" }} />
                  <col style={{ width: "36%" }} />
                  <col style={{ width: "15%" }} />
                  <col style={{ width: "19%" }} />
                </colgroup>
                <thead className="bg-slate-50">
                  <tr className="border-b border-gray-200">
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      Total Area - Estimated Painted Area
                    </th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      Coverage Controls
                    </th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      Actual Painted Area
                    </th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paintRows.map((row) => {
                    const isUndamaged = row.actualPaintedArea <= 0.01;

                    return (
                      <tr
                        key={row.key}
                        className={
                          isUndamaged ? "bg-emerald-50/70" : "bg-white"
                        }
                      >
                        <td className="px-3 py-2.5 align-middle">
                          <p className="whitespace-nowrap text-[13px] text-gray-600">
                            <span className="font-semibold text-gray-900">
                              {row.identifier}
                            </span>
                            <span> - </span>
                            <span>{formatNumber(row.totalArea)} m²</span>
                            <span> - </span>
                            <span>
                              {formatNumber(row.estimatedPaintedArea)} m²
                            </span>
                          </p>
                        </td>
                        <td className="px-3 py-2.5 align-middle">
                          <div className="flex flex-nowrap items-center gap-1.5 whitespace-nowrap">
                            <div className="flex shrink-0 items-center overflow-hidden rounded-md border border-gray-200 shadow-sm">
                              <button
                                type="button"
                                onClick={() =>
                                  adjustPaintedPercentage(row.key, -10)
                                }
                                className="px-2.5 py-1.5 text-[13px] text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                                disabled={row.totalArea <= 0}
                              >
                                -10
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  adjustPaintedPercentage(row.key, -5)
                                }
                                className="border-l border-gray-200 px-2.5 py-1.5 text-[13px] text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                                disabled={row.totalArea <= 0}
                              >
                                -5
                              </button>
                            </div>

                            <div className="relative shrink-0">
                              <input
                                type="number"
                                className="w-20 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1.5 pr-6 text-center text-[13px] font-semibold text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:cursor-not-allowed disabled:opacity-50"
                                value={row.actualPaintedPercentage}
                                min="0"
                                max="100"
                                step="1"
                                onChange={(e) =>
                                  syncActualPaintedPercentage(
                                    row.key,
                                    e.target.value,
                                  )
                                }
                                disabled={row.totalArea <= 0}
                              />
                              <span className="absolute inset-y-0 right-2.5 flex items-center text-[11px] font-semibold text-blue-600">
                                %
                              </span>
                            </div>

                            <div className="flex shrink-0 items-center overflow-hidden rounded-md border border-gray-200 shadow-sm">
                              <button
                                type="button"
                                onClick={() =>
                                  adjustPaintedPercentage(row.key, 5)
                                }
                                className="border-r border-gray-200 px-2.5 py-1.5 text-[13px] text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                                disabled={row.totalArea <= 0}
                              >
                                +5
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  adjustPaintedPercentage(row.key, 10)
                                }
                                className="px-2.5 py-1.5 text-[13px] text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                                disabled={row.totalArea <= 0}
                              >
                                +10
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 align-middle">
                          <div className="flex max-w-[150px] items-center gap-1.5">
                            <input
                              type="number"
                              className="w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:cursor-not-allowed disabled:opacity-50"
                              value={row.actualPaintedArea}
                              min="0"
                              max={row.totalArea || 0}
                              step="0.25"
                              onChange={(e) =>
                                syncActualPaintedArea(row.key, e.target.value)
                              }
                              disabled={row.totalArea <= 0}
                            />
                            <span className="text-[13px] text-gray-500">
                              m²
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 align-middle">
                          <div className="flex flex-col items-start gap-2">
                            {isGroupSelection && isUndamaged && (
                              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap text-emerald-700">
                                <FontAwesomeIcon icon={faCheck} />
                                Undamaged
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => resetToEstimated(row.key)}
                              className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                              disabled={row.totalArea <= 0}
                            >
                              <FontAwesomeIcon icon={faUndo} />
                              Reset
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                {isGroupSelection ? "Selected Elements Area" : "Element Area"}
              </p>
              <p className="mt-1 text-2xl font-bold text-blue-600">
                {formatNumber(totals.totalArea)} m²
              </p>
              <p className="mt-1 text-xs text-gray-500">
                {isGroupSelection
                  ? `Combined total for ${paintRows.length} selected row${paintRows.length === 1 ? "" : "s"}`
                  : `Total area of ${targetIdentifier}`}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Total Estimated Painted Area
              </p>
              <p className="mt-1 text-2xl font-bold text-orange-600">
                {formatNumber(totals.estimatedPaintedArea)} m²
              </p>
              <p className="mt-1 text-xs text-gray-500">
                {formatNumber(totalEstimatedPaintedPercentage, 1)}% of selected
                area
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Total Actual Painted Area
              </p>
              <p className="mt-1 text-2xl font-bold text-emerald-600">
                {formatNumber(totals.actualPaintedArea)} m²
              </p>
              <p className="mt-1 text-xs text-gray-500">
                {formatNumber(totalActualPaintedPercentage, 1)}% of selected
                area
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Painted vs Estimated
              </p>
              <p
                className={`mt-1 text-2xl font-bold ${paintedAreaVarianceColor}`}
              >
                {paintedAreaVariance > 0 ? "+" : ""}
                {formatNumber(paintedAreaVariance)} m²
              </p>
              <p className="mt-1 text-xs text-gray-500">
                {paintedAreaVarianceLabel}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-white"
            disabled={saving}
            type="button"
          >
            Cancel
          </button>
          <div className="flex items-center gap-3">
            {canShowFooterCompletionActions && (
              <button
                onClick={() => handleSave("undamaged")}
                className="flex items-center gap-2 rounded-md border border-emerald-200 bg-white px-5 py-2 text-emerald-700 transition-colors hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={
                  saving ||
                  paintRows.length === 0 ||
                  paintRows.every((row) => row.totalArea <= 0)
                }
                type="button"
              >
                <FontAwesomeIcon icon={faCheck} />
                {saving && activeSubmitMode === "undamaged"
                  ? "Marking Undamaged..."
                  : "Mark Undamaged"}
              </button>
            )}
            {canShowFooterCompletionActions && (
              <button
                onClick={() => handleSave("complete")}
                className="flex items-center gap-2 rounded-md bg-emerald-600 px-5 py-2 text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={
                  saving ||
                  paintRows.length === 0 ||
                  paintRows.every((row) => row.totalArea <= 0)
                }
                type="button"
              >
                <FontAwesomeIcon icon={faCheck} />
                {saving && activeSubmitMode === "complete"
                  ? "Marking Completed..."
                  : "Mark Completed"}
              </button>
            )}
            <button
              onClick={() => handleSave(isCompletionFlow ? "complete" : "save")}
              className={`flex items-center gap-2 rounded-md px-5 py-2 text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                isCompletionFlow
                  ? "bg-emerald-600 hover:bg-emerald-700"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
              disabled={
                saving ||
                paintRows.length === 0 ||
                paintRows.every((row) => row.totalArea <= 0)
              }
              type="button"
            >
              <FontAwesomeIcon icon={faCheck} />
              {saving
                ? activeSubmitMode === "complete"
                  ? "Marking Completed..."
                  : activeSubmitMode === "undamaged"
                    ? "Marking Undamaged..."
                    : "Saving..."
                : submitButtonLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PanelBayPaintingModal;
